import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { QRCodeCanvas } from 'qrcode.react';
import { getProfile } from 'utilities';

// Same scheme the printed card / PDF function uses (WMC-PDF-GCF).
const EMERGENCY_BASE_URL =
  process.env.REACT_APP_EMERGENCY_URL || 'https://emergency.worldmedicalcard.io';

export const ShareMedicalModal = ({ open, onClose, userData }) => {
  const [copied, setCopied] = useState(false);
  const qrWrapRef = useRef(null);

  const memberId =
    userData?.userId || userData?.uid || getProfile()?.uid || '';
  const emergencyUrl = memberId ? `${EMERGENCY_BASE_URL}/${memberId}` : '';
  const fullName = [userData?.firstName, userData?.lastName]
    .filter(Boolean)
    .join(' ');

  const handleDownloadQR = () => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `wmc-medical-qr-${memberId || 'card'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleCopyLink = () => {
    if (!emergencyUrl) return;
    navigator.clipboard?.writeText(emergencyUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleSendEmail = () => {
    if (!emergencyUrl) return;
    const subject = encodeURIComponent('My online medical card');
    const body = encodeURIComponent(
      `Hello, here is my online medical card - ${emergencyUrl}`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Share medical data
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: 'absolute', right: 8, top: 8, color: '#98A2B3' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p style={{ color: '#667085', fontSize: 14, marginTop: 0 }}>
          {fullName
            ? <>Scan this code to open the emergency medical card for <b>{fullName}</b>.</>
            : 'Scan this code to open the emergency medical card.'}
        </p>
        <div
          ref={qrWrapRef}
          style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}
        >
          {emergencyUrl ? (
            <QRCodeCanvas value={emergencyUrl} size={220} includeMargin level="M" />
          ) : (
            <span style={{ color: '#b91c1c', fontSize: 13 }}>
              No card ID found for this account.
            </span>
          )}
        </div>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}
      >
        <Button
          onClick={handleSendEmail}
          disabled={!emergencyUrl}
          startIcon={<EmailIcon />}
          sx={{ textTransform: 'none', mx: 0 }}
        >
          Send email
        </Button>
        <Button
          onClick={handleCopyLink}
          disabled={!emergencyUrl}
          startIcon={<ContentCopyIcon />}
          sx={{ textTransform: 'none', mx: 0 }}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
        <Button
          onClick={handleDownloadQR}
          disabled={!emergencyUrl}
          startIcon={<DownloadIcon />}
          sx={{ textTransform: 'none', mx: 0 }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
